import React, { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../../../redux/store';

import { getCreditApplicationMonitor } from '../../../../redux/services/creditApplicationsMonitorService';
import { File, TrendDown, CheckCircle, TrendUp, Minus } from 'phosphor-react';
import { COLORS } from '../../../../utils/colors';
import AppPillStatus from '../CreditApplicationsMonitor/AppPillStatus';
import moment from 'moment';
import CreditMonitorTile from './CreditMonitorTile';
import { formatMoney } from '@dill/dill-shared';

interface ProgressBarProps {
  value: number;
}

const ProgressBar: React.FC<ProgressBarProps> = ({ value }) => {
  const [width, setWidth] = useState('0%');

  useEffect(() => {
    const timer = setTimeout(() => {
      setWidth(`${value}%`);
    }, 200);
    return () => clearTimeout(timer);
  }, [value]);

  return (
    <div className="flex flex-col items-center justify-center w-full">
      <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden shadow-inner">
        <div
          className="h-full flex items-center justify-center text-white font-medium rounded-l-full transition-all duration-1000 ease-in-out delay-300 
            PRIMARY_500-BG"
          style={{ width }}></div>
      </div>
    </div>
  );
};

const CreditApplicationMonitorDetails = () => {
  const location = useLocation();

  // const [currentCreditApplicationMonitorId, setCurrentCreditApplicationMonitorId] =
  //   useState<string>('');

  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.auth);
  const currentSupplier = useAppSelector((state) => state.auth.currentSupplier);
  const { creditApplicationMonitor } = useAppSelector((state) => state.creditApplicationsMonitor);

  useEffect(() => {
    const pathlist = location.pathname.split('/');
    const creditApplicationMonitorId = pathlist[pathlist.length - 1];
    // setCurrentCreditApplicationMonitorId(creditApplicationMonitorId);
    if (currentSupplier?.id) {
      dispatch(
        getCreditApplicationMonitor({
          supplierId: currentSupplier?.id || '',
          creditApplicationMonitorId: creditApplicationMonitorId,
        })
      );
    }
  }, [location, currentSupplier?.id]);

  const getFirstTwoLetters = (name: string) => {
    return name
      .split(' ')
      .slice(0, 2)
      .map((word) => word.charAt(0).toUpperCase())
      .join('');
  };

  const lastUpdate = useMemo(() => {
    if (!creditApplicationMonitor?.updatedAt) return '';

    const updatedAt = moment(creditApplicationMonitor.updatedAt);
    const now = moment();
    const diffMinutes = now.diff(updatedAt, 'minutes');

    if (diffMinutes < 1) return 'Last updated just now';
    if (diffMinutes < 60) {
      return `Last updated ${diffMinutes} minute${diffMinutes > 1 ? 's' : ''} ago`;
    }

    const diffHours = now.diff(updatedAt, 'hours');
    if (diffHours < 24) {
      return `Last updated ${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    }

    const diffDays = now.diff(updatedAt, 'days');
    return `Last updated ${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
  }, [creditApplicationMonitor?.updatedAt]);

  const creditRiskMonitoringList = useMemo(() => {
    const list: {
      title: string;
      subTitle: string;
      details?: string;
      status: 'SUCCESS' | 'DISABLED' | 'ERROR' | 'INFO' | 'WARNING';
      riskLabel: string;
      type:
        | 'trend-down'
        | 'trend-up'
        | 'trend-none'
        | 'file'
        | 'warning'
        | 'error'
        | 'info'
        | 'success'
        | 'disabled';
    }[] = [];
    if (creditApplicationMonitor?.summary && creditApplicationMonitor.summary.length > 0) {
      const sortedSummary = [...creditApplicationMonitor.summary].sort((a, b) => {
        const dateA = a?.syncDate?.date ? new Date(a.syncDate.date) : new Date(0);
        const dateB = b?.syncDate?.date ? new Date(b.syncDate.date) : new Date(0);
        return dateB.getTime() - dateA.getTime();
      });
      const summary = sortedSummary[0];
      if (summary?.creditScore) {
        const previousCreditScore = summary.creditScore.previousCreditScore ?? 0;
        const currentCreditScore = summary.creditScore.currentCreditScore ?? 0;
        const change = Number(currentCreditScore) - Number(previousCreditScore);
        const percentChange = (change / Number(previousCreditScore)) * 100;
        const absChange = Math.abs(change);
        list.push({
          title: `Credit score ${
            change > 0 && summary?.creditScore?.previousCreditScore
              ? 'increased'
              : change < 0 && summary?.creditScore?.previousCreditScore
              ? 'dropped'
              : ''
          }`,
          subTitle: `Credit score ${
            change > 0 && summary?.creditScore?.previousCreditScore
              ? 'increased'
              : change < 0 && summary?.creditScore?.previousCreditScore
              ? 'dropped'
              : ''
          } ${
            change !== 0 && summary?.creditScore?.previousCreditScore
              ? `by ${absChange} points (${percentChange.toFixed(
                  0
                )}%) from ${previousCreditScore} to ${currentCreditScore}`
              : `is ${currentCreditScore} points`
          }`,
          status:
            percentChange > 0 && summary?.creditScore?.previousCreditScore
              ? 'SUCCESS'
              : percentChange < -10 && summary?.creditScore?.previousCreditScore
              ? 'WARNING'
              : percentChange < 0 &&
                percentChange > -10 &&
                summary?.creditScore?.previousCreditScore
              ? 'WARNING'
              : 'DISABLED',
          type:
            change > 0 && summary?.creditScore?.previousCreditScore
              ? 'trend-up'
              : change < 0 && summary?.creditScore?.previousCreditScore
              ? 'trend-down'
              : 'trend-none',
          details: 'Credit Bureau',
          riskLabel:
            percentChange > 0 && summary?.creditScore?.previousCreditScore
              ? 'Good'
              : percentChange < -10 && summary?.creditScore?.previousCreditScore
              ? 'Medium'
              : percentChange < 0 &&
                percentChange > -10 &&
                summary?.creditScore?.previousCreditScore
              ? 'Medium'
              : 'None',
        });
      }
      if (summary?.plaidBankBalance) {
        const previousBalance = summary.plaidBankBalance.previousAvailableBalance ?? 0;
        const currentBalance = summary.plaidBankBalance.availableBalance ?? 0;
        const change = Number(currentBalance) - Number(previousBalance);
        const percentChange = (change / Number(previousBalance)) * 100;
        const absAmount = Math.abs(change);
        list.push({
          title: `Bank balance ${
            change > 0 && summary.plaidBankBalance.previousAvailableBalance !== null
              ? 'increased'
              : change < 0 && summary.plaidBankBalance.previousAvailableBalance !== null
              ? 'dropped'
              : ''
          }`,
          subTitle: `Bank balance ${
            change > 0 && summary.plaidBankBalance.previousAvailableBalance !== null
              ? 'increased'
              : change < 0 && summary.plaidBankBalance.previousAvailableBalance !== null
              ? 'dropped'
              : ''
          } ${
            change !== 0 && summary.plaidBankBalance.previousAvailableBalance !== null
              ? `by ${formatMoney(absAmount, 2)} (${percentChange.toFixed(
                  0
                )}%) from ${previousBalance} to ${currentBalance}`
              : `of ${formatMoney(currentBalance, 2)}`
          }`,
          status:
            percentChange > 0 && summary.plaidBankBalance.previousAvailableBalance !== null
              ? 'SUCCESS'
              : percentChange < -25 && summary.plaidBankBalance.previousAvailableBalance !== null
              ? 'WARNING'
              : percentChange < 0 &&
                percentChange > -25 &&
                summary.plaidBankBalance.previousAvailableBalance !== null
              ? 'WARNING'
              : 'DISABLED',
          type:
            change > 0 && summary.plaidBankBalance.previousAvailableBalance !== null
              ? 'trend-up'
              : change < 0 && summary.plaidBankBalance.previousAvailableBalance !== null
              ? 'trend-down'
              : 'trend-none',
          details: 'Plaid',
          riskLabel:
            percentChange > 0 && summary.plaidBankBalance.previousAvailableBalance !== null
              ? 'Good'
              : percentChange < -25 && summary.plaidBankBalance.previousAvailableBalance !== null
              ? 'Medium'
              : percentChange < 0 &&
                percentChange > -25 &&
                summary.plaidBankBalance.previousAvailableBalance !== null
              ? 'Medium'
              : 'None',
        });
      }
      if (summary?.bankruptcy) {
        list.push({
          title: 'Bankruptcy Alert',
          subTitle: `Company has declared bankruptcy on ${summary?.bankruptcy?.bankruptcyDate}`,
          status: 'ERROR',
          type: 'error',
          details: 'Credit Bureau',
          riskLabel: 'High',
        });
      }
      if (summary?.lien) {
        list.push({
          title: 'Lien Filing Alert',
          subTitle: `Company has seen a lien filing on ${summary?.lien?.lienDate} ${
            summary?.lien?.amount ? `for ${formatMoney(summary?.lien?.amount, 2)}` : ''
          }`,
          status: 'ERROR',
          type: 'error',
          details: 'Credit Bureau',
          riskLabel: 'High',
        });
      }
      if (summary?.claim) {
        list.push({
          title: 'Claim Alert',
          subTitle: `Company has seen claim on ${summary?.claim?.claimDate}`,
          status: 'ERROR',
          type: 'error',
          details: 'Credit Bureau',
          riskLabel: 'High',
        });
      }
      if (summary?.uccFiling) {
        list.push({
          title: 'UCC Filing Alert',
          subTitle: `Company has seen a UCC Filing on ${summary?.uccFiling?.uccFilingDate}`,
          status: 'ERROR',
          type: 'error',
          details: 'Credit Bureau',
          riskLabel: 'High',
        });
      }
    }
    return list;
  }, [creditApplicationMonitor]);

  const allAlerts = useMemo(() => {
    return creditRiskMonitoringList.filter((item) => item.riskLabel === 'High').length;
  }, [creditRiskMonitoringList]);

  const creditSore = useMemo(() => {
    let points = '';
    let trend: 'trend-up' | 'trend-down' | 'trend-none' = 'trend-none';
    if (creditApplicationMonitor?.summary?.length) {
      const sortedSummary = [...creditApplicationMonitor.summary].sort((a, b) => {
        const dateA = a?.syncDate?.date ? new Date(a.syncDate.date) : new Date(0);
        const dateB = b?.syncDate?.date ? new Date(b.syncDate.date) : new Date(0);
        return dateB.getTime() - dateA.getTime();
      });
      const summary = sortedSummary[0];
      if (summary?.creditScore) {
        const previousCreditScore = summary.creditScore.previousCreditScore ?? 0;
        const currentCreditScore = summary.creditScore.currentCreditScore ?? 0;
        const change = Number(currentCreditScore) - Number(previousCreditScore);
        const percentChange = (change / Number(previousCreditScore)) * 100;
        if (summary.creditScore.previousCreditScore !== null && change !== 0) {
          points = `${change} pts (${percentChange.toFixed(0)}%)`;
          trend = change > 0 ? 'trend-up' : change < 0 ? 'trend-down' : 'trend-none';
        }
      }
    }

    const cmsClass = creditApplicationMonitor?.nacmReport?.CmsCRScoreDetail?.Class;
    const status =
      cmsClass === '1' || cmsClass === '2'
        ? ('SUCCESS' as const)
        : cmsClass === '3'
        ? ('WARNING' as const)
        : cmsClass === '4A' || cmsClass === '4B' || cmsClass === '5'
        ? ('ERROR' as const)
        : ('DISABLED' as const);

    return {
      status,
      label: creditApplicationMonitor?.nacmReport?.CmsCRScoreDetail?.ClassType ?? 'Risk N/A',
      score: creditApplicationMonitor?.nacmReport?.CmsCRScoreDetail?.Score ?? 'N/A',
      points,
      trend,
    };
  }, [creditApplicationMonitor?.nacmReport, creditApplicationMonitor?.summary]);

  return (
    <div className="h-full p-4 flex flex-col overflow-y-scroll">
      <div className="p-4 w-full bg-white rounded-lg mb-4">
        <div className="w-full flex justify-between">
          <div className="flex gap-4 items-center">
            <div className="font-bold PRIMARY_100-BG PRIMARY_500-CLR h-8 w-8 text-xs flex justify-center items-center rounded-full">
              {getFirstTwoLetters(creditApplicationMonitor?.companyName ?? '')}
            </div>
            <div>
              <div className="font-bold text-sm">{creditApplicationMonitor?.companyName ?? ''}</div>
              <div className="text-xs GREY_500-CLR">{lastUpdate}</div>
            </div>
          </div>
          <div>
            <div className="text-[10px] mb-1">CREDIT SCORE</div>
            <div className="flex gap-2 items-center mb-1">
              <div className="font-bold text-xl">{creditSore.score}</div>
              {creditSore.trend === 'trend-down' && (
                <TrendDown size={16} weight="bold" color={COLORS.ERROR_500} />
              )}
              {creditSore.trend === 'trend-up' && (
                <TrendUp size={16} weight="bold" color={COLORS.SUCCESS_500} />
              )}
              {creditSore.trend === 'trend-none' && (
                <Minus size={16} weight="bold" color={COLORS.GREY_500} />
              )}
              <div className="text-xs GREY_500-CLR">
                <AppPillStatus
                  status={creditSore.status}
                  label={creditSore.label}
                  wrapperClassName="w-fit"
                />
              </div>
            </div>
            {creditSore.points && <div className="text-xs GREY_500-CLR">{creditSore.points}</div>}
          </div>
        </div>
        <div className="font-bold mb-1">Credit Overview</div>
        <div className="w-full flex justify-between gap-2">
          <div className="w-full border p-2 rounded-lg">
            <div className="text-sm GREY_500-CLR">Total Credit Exposure</div>
            <div className="text-lg font-bold mb-2">$87,450</div>
            <div className="text-sm GREY_500-CLR mb-1">Credit Utilization</div>
            <div className="w-full flex justify-between mb-0.5">
              <div className="text-xs font-semibold">27.1%</div>
              <div className="text-xs font-semibold GREY_500-CLR">of 87,450</div>
            </div>
            <ProgressBar value={27.1} />
          </div>
          <div className="w-full border p-2 rounded-lg">
            <div className="text-sm GREY_500-CLR">Outstanding Balance</div>
            <div className="text-lg font-bold mb-2 ERROR_500-CLR">$23,6800</div>
            <div className="text-sm GREY_500-CLR mb-1">Payment Timeliness</div>
            <div className="w-full flex justify-between mb-0.5">
              <div className="text-xs font-semibold">On-time</div>
              <div className="text-xs font-semibold SUCCESS_500-CLR">94.2%</div>
            </div>
            <ProgressBar value={94.2} />
          </div>
        </div>
      </div>
      <div className="w-full rounded-lg flex gap-4 h-full">
        <div className="w-3/5 p-2 rounded-lg bg-white flex flex-col gap-2 h-full">
          <div className="w-full flex justify-between">
            <div className="text-base font-semibold">Credit Risk Monitoring</div>
            {allAlerts > 0 && (
              <AppPillStatus
                status="ERROR"
                label={`${allAlerts} High Risk Alert${allAlerts > 1 ? 's' : ''}`}
                wrapperClassName="w-fit"
              />
            )}
          </div>
          {creditRiskMonitoringList.map((item, index) => (
            <div key={`credit-monitor-tile-${index}`}>
              <CreditMonitorTile
                title={item.title}
                subTitle={item.subTitle}
                details={item.details}
                status={item.status}
                riskLabel={item.riskLabel}
                type={item.type}
              />
            </div>
          ))}
        </div>
        <div className="w-2/5 p-2 rounded-lg bg-white flex flex-col gap-2 h-full">
          <div className="w-full flex justify-between">
            <div className="text-base font-semibold">Fraud Detection</div>
            <AppPillStatus status="ERROR" label="1 High Risk Alerts" wrapperClassName="w-fit" />
          </div>
          <div className="w-full border p-2 flex rounded-lg justify-between">
            <div className="flex gap-2">
              <div className="rounded-full SUCCESS_100-BG SUCCESS_500-CLR h-8 w-8 text-xs flex justify-center items-center">
                <CheckCircle size={18} weight="fill" color={COLORS.SUCCESS_500} />
              </div>
              <div>
                <div className="font-semibold text-sm mb-0.5">Identity Verification</div>
                <div className="text-xs GREY_500-CLR mb-1">
                  Business identity verified through Experian and government databases
                </div>
                <div className="text-xs GREY_500-CLR">Last Checked 1 hour ago</div>
              </div>
            </div>
            <AppPillStatus status="SUCCESS" label="Passed" wrapperClassName="w-fit" />
          </div>
          <div className="w-full border p-2 flex rounded-lg justify-between">
            <div className="flex gap-2">
              <div className="rounded-full WARNING_200-BG WARNING_500-CLR h-8 w-8 text-xs flex justify-center items-center">
                <File size={16} weight="fill" color={COLORS.WARNING_500} />
              </div>
              <div>
                <div className="font-semibold text-sm mb-0.5">Bank Account Verification</div>
                <div className="text-xs GREY_500-CLR mb-1">
                  Unusual transaction patterns detected in recent activity
                </div>
                <div className="text-xs GREY_500-CLR">Last Checked 2 hours ago</div>
              </div>
            </div>
            <AppPillStatus status="WARNING" label="Medium" wrapperClassName="w-fit" />
          </div>
          <div className="w-full border p-2 flex rounded-lg justify-between">
            <div className="flex gap-2">
              <div className="rounded-full WARNING_200-BG WARNING_500-CLR h-8 w-8 text-xs flex justify-center items-center">
                <File size={16} weight="fill" color={COLORS.WARNING_500} />
              </div>
              <div>
                <div className="font-semibold text-sm mb-0.5">Bank Balance Alert</div>
                <div className="text-xs GREY_500-CLR mb-1">
                  Bank balance declined by 58% in the last 30 days
                </div>
                <div className="text-xs GREY_500-CLR">Credit Bureau | 1 Day ago</div>
              </div>
            </div>
            <AppPillStatus status="WARNING" label="Medium" wrapperClassName="w-fit" />
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreditApplicationMonitorDetails;
